import { Download, FileText } from 'lucide-react';
import { formatDate, formatFileSize } from '@blimo/shared';
import { Badge } from './badge.jsx';
import { cn } from '../lib/utils.js';

/**
 * List of documents (DocumentResource) with download link, optional "open" action and row actions.
 * @param {{
 *   documents?: Array<{ id: number, title?: string|null, original_name?: string|null, mime_type?: string|null, size?: number|null, created_at?: string|null, published_at?: string|null }>,
 *   hrefFor: (doc: any) => string,
 *   onOpen?: (doc: any) => void,
 *   activeId?: string|number|null,
 *   actions?: (doc: any) => import('react').ReactNode,
 *   showPublished?: boolean,
 *   emptyText?: string,
 *   label?: string,
 *   className?: string,
 * }} props
 */
export function DocumentList({ documents, hrefFor, onOpen, activeId, actions, showPublished = false, emptyText = 'Keine Dokumente.', label, className }) {
  if (!documents?.length) return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  return (
    <ul aria-label={label} data-slot="document-list" className={cn('flex flex-col divide-y rounded-md border', className)}>
      {documents.map((doc) => {
        const name = doc.title || doc.original_name || `Dokument #${doc.id}`;
        const active = activeId != null && String(activeId) === String(doc.id);
        return (
          <li key={doc.id} className={cn('flex flex-wrap items-center gap-2 px-3 py-2 text-sm', active && 'bg-accent')}>
            <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="flex min-w-0 flex-1 flex-col">
              {onOpen ? (
                <button
                  type="button"
                  onClick={() => onOpen(doc)}
                  aria-current={active ? 'true' : undefined}
                  className="truncate text-left font-medium text-primary hover:underline"
                >
                  {name}
                </button>
              ) : (
                <span className="truncate font-medium">{name}</span>
              )}
              <span className="text-xs text-muted-foreground">
                {[doc.created_at ? formatDate(doc.created_at) : null, doc.size != null ? formatFileSize(doc.size) : null].filter(Boolean).join(' · ')}
              </span>
            </span>
            {showPublished && (doc.published_at ? <Badge variant="success">Veröffentlicht</Badge> : <Badge variant="neutral">Nicht veröffentlicht</Badge>)}
            {actions?.(doc)}
            <a
              href={hrefFor(doc)}
              className="inline-flex size-(--control-height-sm) items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label={`${name} herunterladen`}
              title="Herunterladen"
              download
            >
              <Download className="size-4" aria-hidden="true" />
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Inline view of a document (PDF/image) via the download endpoint with `?inline=1`; other types offer the download.
 * @param {{ src: string, downloadHref: string, title: string, mimeType?: string|null, className?: string }} props
 */
export function DocumentViewer({ src, downloadHref, title, mimeType, className }) {
  const viewable = !mimeType || mimeType === 'application/pdf' || mimeType.startsWith('image/');
  return (
    <div className={cn('flex min-h-0 flex-col gap-2', className)}>
      {viewable ? (
        <iframe title={title} src={src} className="h-[70dvh] min-h-80 w-full rounded-md border bg-white" />
      ) : (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">Für diesen Dateityp gibt es keine Vorschau.</p>
      )}
      <a href={downloadHref} download className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
        <Download className="size-4" aria-hidden="true" /> {title} herunterladen
      </a>
    </div>
  );
}
